import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useHttp, useAuthContext, useAppContext } from '@veda/dashboard-ui';

function ErrorBox({ msg }) {
  return (
    <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
      {msg}
    </div>
  );
}

/**
 * Pagina di login riservata al Superadmin.
 * URL: /superadmin — non linkato dall'app, noto solo all'amministratore.
 * Usa il login Veda standard (email + password → /api/dashboard/login).
 */
export default function LoginSuperadmin() {
  const http           = useHttp();
  const { logIn }      = useAuthContext();
  const { currentMode } = useAppContext();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [form,     setForm]     = useState({ email: '', password: '' });
  const [errore,   setErrore]   = useState(null);
  const [recovery, setRecovery] = useState(false);
  const [emailRec, setEmailRec] = useState('');
  const [success,  setSuccess]  = useState(null);

  const aggiorna = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  async function handleLogin(e) {
    e.preventDefault();
    setErrore(null);
    await http.request('/sanctum/csrf-cookie', { method: 'get' });
    await http.request('/api/dashboard/login', {
      method: 'post',
      body: JSON.stringify(form),
      onSuccess: (res) => {
        logIn(res);
        const redirect = searchParams.get('redirect');
        if (redirect) setTimeout(() => navigate(redirect, { replace: true }), 0);
      },
      // Errori HTTP (401 credenziali, 409 sessione di un altro utente già attiva, 422)
      onFormError: (res) => setErrore(res?.message ?? 'Credenziali non valide.'),
      onError: (err) => setErrore(err instanceof Error ? err.message : 'Credenziali non valide.'),
    });
  }

  async function handleRecovery(e) {
    e.preventDefault();
    setErrore(null);
    await http.request('/sanctum/csrf-cookie', { method: 'get' });
    await http.request('/api/dashboard/forgot-password', {
      method: 'post',
      body: JSON.stringify({ email: emailRec }),
      onSuccess: () => setSuccess('Ti abbiamo contattato via mail per il recupero password.'),
      onError: (err) => setErrore(err instanceof Error ? err.message : 'Errore durante la richiesta.'),
    });
  }

  return (
    <div className={currentMode}>
      <div className="min-h-screen flex items-center justify-center dark:bg-main-dark-bg bg-main-bg p-8">
        <div className="w-full max-w-sm">

          <div className="mb-8 text-center">
            <span className="text-2xl font-extrabold text-gray-800 dark:text-white">Qualifia</span>
            <p className="text-xs text-gray-400 mt-1">Area amministrazione</p>
          </div>

          {recovery ? (
            <form onSubmit={handleRecovery} className="flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-1">Reimposta password</h2>
                <p className="text-sm text-gray-400">Inserisci la tua email per ricevere il link di recupero.</p>
              </div>
              {errore  && <ErrorBox msg={errore} />}
              {success && <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}
              <input
                type="email" value={emailRec} onChange={(e) => setEmailRec(e.target.value)}
                placeholder="Email" required
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <button type="submit" disabled={http.loading} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {http.loading ? 'Invio in corso…' : 'Invia link di recupero'}
              </button>
              <button type="button" onClick={() => setRecovery(false)} className="text-xs text-center text-gray-500 hover:underline">
                ← Torna al login
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="flex flex-col gap-4">
              {errore && <ErrorBox msg={errore} />}
              <input
                type="email" name="email" value={form.email} onChange={aggiorna}
                placeholder="Email" required autoFocus
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <input
                type="password" name="password" value={form.password} onChange={aggiorna}
                placeholder="Password" required
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <button type="submit" disabled={http.loading} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {http.loading ? 'Accesso in corso…' : 'Accedi'}
              </button>
              <button type="button" onClick={() => setRecovery(true)} className="text-xs text-center text-gray-500 hover:underline">
                Password dimenticata?
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
